import { Input, InputNumber, Select, Space, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { DefaultOptionType } from 'antd/lib/select';
import React from 'react';

import { DragIcon, RemoveIcon } from '../../iconComponents';
import sharedStyles from '../../sharedStyles.module.scss';
import type { FreeItemTableData, PaidItemTableData } from '../types';
import styles from './index.module.scss';

const { Text } = Typography;

const updateFreeItemInfo = (args: {
  value: string;
  dataUpdater: React.Dispatch<React.SetStateAction<FreeItemTableData[]>>;
  dataIndex: 'itemName' | 'ticketId';
}) => {
  const { value, dataUpdater, dataIndex } = args;
  if (dataIndex === 'itemName') {
    dataUpdater((prevState) => [{ ...prevState[0], [dataIndex]: value }]);
  }
  if (dataIndex === 'ticketId') {
    dataUpdater((prevState) => [
      { ...prevState[0], [dataIndex]: value ? [value] : [] },
    ]);
  }
};

const checkIsItemFieldsEmpty = (args: {
  item: PaidItemTableData;
  isCheckTaiwanPay: boolean;
  isCheckPoint: boolean;
}) => {
  const { item, isCheckTaiwanPay, isCheckPoint } = args;
  const {
    itemName,
    taiwanPayPrice,
    pointPrice,
    reservationLimits: { reservationLowerLimit, reservationUpperLimit },
  } = item;

  if (isCheckTaiwanPay && isCheckPoint === false) {
    return (
      !itemName &&
      typeof taiwanPayPrice !== 'number' &&
      typeof reservationLowerLimit !== 'number' &&
      typeof reservationUpperLimit !== 'number'
    );
  }
  if (isCheckTaiwanPay === false && isCheckPoint) {
    return (
      !itemName &&
      typeof pointPrice !== 'number' &&
      typeof reservationLowerLimit !== 'number' &&
      typeof reservationUpperLimit !== 'number'
    );
  }
  if (isCheckTaiwanPay === false && isCheckPoint === false) {
    return (
      !itemName &&
      typeof reservationLowerLimit !== 'number' &&
      typeof reservationUpperLimit !== 'number'
    );
  }

  return (
    !itemName &&
    typeof taiwanPayPrice !== 'number' &&
    typeof pointPrice !== 'number' &&
    typeof reservationLowerLimit !== 'number' &&
    typeof reservationUpperLimit !== 'number'
  );
};

export const checkIsAllPaidItemFieldsEmpty = (args: {
  paidItems: PaidItemTableData[];
  isCheckTaiwanPay: boolean;
  isCheckPoint: boolean;
}) => {
  const { paidItems, isCheckTaiwanPay, isCheckPoint } = args;

  const displayFalseItems = paidItems.filter(
    (item) => item.display === false && item.itemId.includes('item') === false
  );
  const onlyOneDisplayTrueItem = paidItems.filter((item) =>
    item.itemId.includes('item')
  );

  if (
    paidItems.length - displayFalseItems.length === 1 &&
    onlyOneDisplayTrueItem.length === 1
  ) {
    const isItemFieldsEmpty = checkIsItemFieldsEmpty({
      item: onlyOneDisplayTrueItem[0],
      isCheckTaiwanPay,
      isCheckPoint,
    });

    return isItemFieldsEmpty;
  }

  const result = paidItems.every((item) => {
    if (item.display === false) {
      return true;
    }
    const isItemFieldsEmpty = checkIsItemFieldsEmpty({
      item,
      isCheckTaiwanPay,
      isCheckPoint,
    });
    return isItemFieldsEmpty;
  });

  return result;
};

export const getFreeItemColumns = (args: {
  ticketOptions: DefaultOptionType[] | undefined;
  updateFreeItem: React.Dispatch<React.SetStateAction<FreeItemTableData[]>>;
  isValidating: boolean;
  isAllItemFieldsEmpty: boolean;
  updateIsValidating: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const {
    ticketOptions,
    updateFreeItem,
    isValidating,
    isAllItemFieldsEmpty,
    updateIsValidating,
  } = args;

  const columns: ColumnsType<FreeItemTableData> = [
    {
      title: '名稱',
      dataIndex: 'itemName',
      width: '50%',
      render: (value, record) => {
        const isAllFreeItemFieldsEmpty =
          !record.itemName && record.ticketId.length === 0;
        return (
          <Space direction="vertical" className={sharedStyles.widthFull}>
            <Input
              status={
                isValidating &&
                !isAllItemFieldsEmpty &&
                !isAllFreeItemFieldsEmpty &&
                !value
                  ? 'error'
                  : ''
              }
              placeholder="請輸入項目名稱"
              onChange={(event) => {
                updateIsValidating(false);
                updateFreeItemInfo({
                  value: event.target.value,
                  dataUpdater: updateFreeItem,
                  dataIndex: 'itemName',
                });
              }}
              value={value}
            />
            {isValidating &&
            !isAllItemFieldsEmpty &&
            !isAllFreeItemFieldsEmpty &&
            !value ? (
              <Text type="danger">請輸入項目名稱</Text>
            ) : (
              <div className={styles.textPlaceholder} />
            )}
          </Space>
        );
      },
    },
    {
      title: '票券',
      dataIndex: 'ticketId',
      width: '50%',
      render: (value, record) => {
        const isAllFreeItemFieldsEmpty =
          !record.itemName && record.ticketId.length === 0;
        return (
          <Space direction="vertical" className={sharedStyles.widthFull}>
            <Select
              allowClear
              options={ticketOptions}
              placeholder="請選擇票券"
              className={sharedStyles.widthFull}
              status={
                isValidating &&
                !isAllItemFieldsEmpty &&
                !isAllFreeItemFieldsEmpty &&
                (value.length === 0 || !value)
                  ? 'error'
                  : ''
              }
              onChange={(value) => {
                updateIsValidating(false);
                updateFreeItemInfo({
                  value,
                  dataUpdater: updateFreeItem,
                  dataIndex: 'ticketId',
                });
              }}
              value={value}
            />
            {isValidating &&
            !isAllItemFieldsEmpty &&
            !isAllFreeItemFieldsEmpty &&
            (value.length === 0 || !value) ? (
              <Text type="danger">請選擇票券</Text>
            ) : (
              <div className={styles.textPlaceholder} />
            )}
          </Space>
        );
      },
    },
  ];

  return columns;
};

export const getPaidItemTableColumns = (args: {
  updatePaidItems: (args: {
    itemId: string;
    order: number;
    value: string | number | null;
    dataIndex: string;
  }) => void;
  isValidating: boolean;
  isCheckTaiwanPay: boolean;
  isCheckPoint: boolean;
  isAllItemFieldsEmpty: boolean;
  paidItems: PaidItemTableData[];
  deletePaidItem: (record: PaidItemTableData) => void;
}) => {
  const {
    updatePaidItems,
    isValidating,
    isCheckTaiwanPay,
    isCheckPoint,
    isAllItemFieldsEmpty,
    paidItems,
    deletePaidItem,
  } = args;
  const isAnyPaymentChecked = isCheckTaiwanPay || isCheckPoint;
  const isAllPaidItemFieldsEmpty = checkIsAllPaidItemFieldsEmpty({
    paidItems,
    isCheckTaiwanPay,
    isCheckPoint,
  });

  const columns: ColumnsType<PaidItemTableData> = [
    {
      title: '名稱',
      dataIndex: 'itemName',
      width: '35%',
      render: (value, record, index) => {
        const isShowError =
          isValidating &&
          !isAllItemFieldsEmpty &&
          !isAllPaidItemFieldsEmpty &&
          !value;
        return (
          <div className={sharedStyles.flexbox}>
            <DragIcon className={sharedStyles.dragIcon} />
            <Space direction="vertical" className={sharedStyles.widthFull}>
              <Input
                status={isShowError && isAnyPaymentChecked ? 'error' : ''}
                placeholder="請輸入項目名稱"
                onChange={(event) =>
                  updatePaidItems({
                    itemId: record.itemId,
                    order: index,
                    value: event.target.value,
                    dataIndex: 'itemName',
                  })
                }
                value={value}
              />
              {isShowError && isAnyPaymentChecked ? (
                <Text type="danger">請輸入項目名稱</Text>
              ) : (
                <div className={styles.textPlaceholder} />
              )}
            </Space>
          </div>
        );
      },
    },
    {
      title: '台幣 (單位:元)',
      dataIndex: 'taiwanPayPrice',
      width: '20%',
      render: (value, record, index) => {
        const isShowError =
          isValidating &&
          isCheckTaiwanPay &&
          !isAllItemFieldsEmpty &&
          !isAllPaidItemFieldsEmpty &&
          typeof value !== 'number';
        return (
          <Space direction="vertical">
            <InputNumber
              min={0}
              placeholder="請輸入價格"
              disabled={!isCheckTaiwanPay}
              className={sharedStyles.widthFull}
              status={isShowError ? 'error' : ''}
              onChange={(value) =>
                updatePaidItems({
                  itemId: record.itemId,
                  order: index,
                  value,
                  dataIndex: 'taiwanPayPrice',
                })
              }
              value={value}
            />
            {isShowError ? (
              <Text type="danger">請輸入價格</Text>
            ) : (
              <div className={styles.textPlaceholder} />
            )}
          </Space>
        );
      },
    },
    {
      title: '點數 (單位:點)',
      dataIndex: 'pointPrice',
      width: '20%',
      render: (value, record, index) => {
        const isShowError =
          isValidating &&
          isCheckPoint &&
          !isAllItemFieldsEmpty &&
          !isAllPaidItemFieldsEmpty &&
          typeof value !== 'number';
        return (
          <Space direction="vertical">
            <InputNumber
              min={0}
              placeholder="請輸入價格"
              disabled={!isCheckPoint}
              className={sharedStyles.widthFull}
              status={isShowError ? 'error' : ''}
              onChange={(value) =>
                updatePaidItems({
                  itemId: record.itemId,
                  order: index,
                  value,
                  dataIndex: 'pointPrice',
                })
              }
              value={value}
            />
            {isShowError ? (
              <Text type="danger">請輸入價格</Text>
            ) : (
              <div className={styles.textPlaceholder} />
            )}
          </Space>
        );
      },
    },
    {
      title: '單筆預約下限與上限數量',
      dataIndex: 'reservationLimits',
      width: '30%',
      render: (_value, record, index) => {
        const { reservationLowerLimit, reservationUpperLimit } =
          record.reservationLimits;
        const isShowLowerLimitError =
          isValidating &&
          !isAllItemFieldsEmpty &&
          !isAllPaidItemFieldsEmpty &&
          typeof reservationLowerLimit !== 'number';

        const isShowUpperLimitError =
          isValidating &&
          !isAllItemFieldsEmpty &&
          !isAllPaidItemFieldsEmpty &&
          typeof reservationUpperLimit !== 'number';

        const isLowerLimitGreatThanUpperLimit =
          typeof reservationLowerLimit === 'number' &&
          typeof reservationUpperLimit === 'number' &&
          reservationLowerLimit > reservationUpperLimit;

        return (
          <div className={`${sharedStyles.flexbox}`}>
            <Space direction="vertical">
              <InputNumber
                min={0}
                placeholder="請輸入單筆預約下限數量"
                className={sharedStyles.inputWidth}
                status={
                  (isShowLowerLimitError && isAnyPaymentChecked) ||
                  (isValidating && isLowerLimitGreatThanUpperLimit)
                    ? 'error'
                    : ''
                }
                onChange={(value) =>
                  updatePaidItems({
                    itemId: record.itemId,
                    order: index,
                    value,
                    dataIndex: 'reservationLowerLimit',
                  })
                }
                value={reservationLowerLimit}
              />
              {(isShowLowerLimitError && isAnyPaymentChecked) ||
              (isValidating && isLowerLimitGreatThanUpperLimit) ? (
                <Text type="danger">
                  {isLowerLimitGreatThanUpperLimit
                    ? '下限數量不可大於上限數量'
                    : '請輸入單筆預約下限數量'}
                </Text>
              ) : (
                <div className={styles.textPlaceholder} />
              )}
            </Space>
            <span> - </span>
            <Space direction="vertical">
              <InputNumber
                min={0}
                placeholder="請輸入單筆預約上限數量"
                className={sharedStyles.inputWidth}
                status={
                  (isShowUpperLimitError && isAnyPaymentChecked) ||
                  (isValidating && isLowerLimitGreatThanUpperLimit)
                    ? 'error'
                    : ''
                }
                onChange={(value) =>
                  updatePaidItems({
                    itemId: record.itemId,
                    order: index,
                    value,
                    dataIndex: 'reservationUpperLimit',
                  })
                }
                value={reservationUpperLimit}
              />
              {(isShowUpperLimitError && isAnyPaymentChecked) ||
              (isValidating && isLowerLimitGreatThanUpperLimit) ? (
                <Text type="danger">
                  {isLowerLimitGreatThanUpperLimit
                    ? '下限數量不可大於上限數量'
                    : '請輸入單筆預約上限數量'}
                </Text>
              ) : (
                <div className={styles.textPlaceholder} />
              )}
            </Space>
            <div onClick={() => deletePaidItem(record)}>
              <RemoveIcon className={sharedStyles.removeIcon} />
            </div>
          </div>
        );
      },
    },
  ];

  return columns;
};

export const updatePaidItemOrder = (
  fromIndex: number,
  toIndex: number,
  paidItems: PaidItemTableData[]
) => {
  const newPaidItems = [...paidItems];
  const displayTrueItems = newPaidItems.filter((item) => item.display);
  const displayFalseItems = newPaidItems.filter(
    (item) => item.display === false
  );

  const [removed] = displayTrueItems.splice(fromIndex, 1);
  displayTrueItems.splice(toIndex, 0, removed);
  const draggedItems = displayTrueItems.concat(displayFalseItems);

  return draggedItems;
};

export const checkIsAllItemFieldsEmpty = (args: {
  freeItem: FreeItemTableData[];
  paidItems: PaidItemTableData[];
  isCheckTaiwanPay: boolean;
  isCheckPoint: boolean;
}) => {
  const { freeItem, paidItems, isCheckTaiwanPay, isCheckPoint } = args;
  const { itemName, ticketId } = freeItem[0];

  const isAllFreeItemFieldsEmpty = !itemName && ticketId.length === 0;
  const isAllPaidItemFieldsEmpty = checkIsAllPaidItemFieldsEmpty({
    paidItems,
    isCheckTaiwanPay,
    isCheckPoint,
  });

  return isAllFreeItemFieldsEmpty && isAllPaidItemFieldsEmpty;
};

export const checkIsPaidItemValuesPassed = (args: {
  paidItems: PaidItemTableData[];
  isCheckTaiwanPay: boolean;
  isCheckPoint: boolean;
}) => {
  const { paidItems, isCheckTaiwanPay, isCheckPoint } = args;

  const result = paidItems.every((item) => {
    const {
      itemName,
      taiwanPayPrice,
      pointPrice,
      reservationLimits: { reservationLowerLimit, reservationUpperLimit },
    } = item;

    if (item.display === false) {
      return true;
    }

    if (isCheckTaiwanPay && isCheckPoint === false) {
      return (
        itemName &&
        typeof taiwanPayPrice === 'number' &&
        typeof reservationLowerLimit === 'number' &&
        typeof reservationUpperLimit === 'number' &&
        reservationLowerLimit <= reservationUpperLimit
      );
    }
    if (isCheckTaiwanPay === false && isCheckPoint) {
      return (
        itemName &&
        typeof pointPrice === 'number' &&
        typeof reservationLowerLimit === 'number' &&
        typeof reservationUpperLimit === 'number' &&
        reservationLowerLimit <= reservationUpperLimit
      );
    }

    if (isCheckTaiwanPay && isCheckPoint) {
      return (
        itemName &&
        typeof taiwanPayPrice === 'number' &&
        typeof pointPrice === 'number' &&
        typeof reservationLowerLimit === 'number' &&
        typeof reservationUpperLimit === 'number' &&
        reservationLowerLimit <= reservationUpperLimit
      );
    }

    const isAllPaidItemFieldsEmpty = checkIsAllPaidItemFieldsEmpty({
      paidItems,
      isCheckTaiwanPay,
      isCheckPoint,
    });

    return (
      (!itemName ||
        (typeof taiwanPayPrice !== 'number' &&
          typeof pointPrice !== 'number') ||
        typeof reservationLowerLimit !== 'number' ||
        typeof reservationUpperLimit !== 'number' ||
        reservationLowerLimit > reservationUpperLimit) === false &&
      !isAllPaidItemFieldsEmpty
    );
  });

  return result;
};

export const restoreEditingItemValuesWhenDelete = (
  record: PaidItemTableData
) => {
  const { name, priceInfo } = record;

  switch (priceInfo?.kind) {
    case 'Both':
      return {
        ...record,
        itemName: name ?? '',
        taiwanPayPrice: priceInfo.taiwanPayPrice,
        pointPrice: priceInfo.pointPrice,
        reservationLimits: {
          reservationLowerLimit: priceInfo.reservationLowerLimit,
          reservationUpperLimit: priceInfo.reservationUpperLimit,
        },
      };
    case 'TaiwanPay':
      return {
        ...record,
        itemName: name ?? '',
        taiwanPayPrice: priceInfo.taiwanPayPrice,
        reservationLimits: {
          reservationLowerLimit: priceInfo.reservationLowerLimit,
          reservationUpperLimit: priceInfo.reservationUpperLimit,
        },
      };
    case 'Point':
      return {
        ...record,
        itemName: name ?? '',
        pointPrice: priceInfo.pointPrice,
        reservationLimits: {
          reservationLowerLimit: priceInfo.reservationLowerLimit,
          reservationUpperLimit: priceInfo.reservationUpperLimit,
        },
      };

    default:
      return record;
  }
};
